/*
  Structured content for the four platform-service pages — Incident Response,
  OpSec Review, Monitor, and Certification. All four share the same page shape
  (hero, optional process strip, capability cards, pull-quote, closing CTA), so
  the copy lives here as data and `PlatformServicePage` renders it. The copy is
  Quantstamp's own framing of these offerings; it is not lifted from any other
  site.

  Voice: measured, complete sentences in an educational, professional register,
  matching the existing Quantstamp service pages (audits, infrastructure-audits,
  economic-exploits). It avoids punchy fragments, em-dash asides, and urgency.

  `cta` chooses which global request flow the buttons open:
    - "audit"    → the site-wide Request a Security Audit modal
    - "pentest"  → the infrastructure pentest contact modal
    - "analysis" → the economic-exploit analysis contact modal
  A `secondaryCta` with `crisis: true` renders the urgent "under attack" link.
*/

import type { ContactModalId } from "@/lib/contact-modals";

export type PlatformCtaKind = "audit" | "pentest" | "analysis";

export type PlatformItem = {
  name: string;
  description: string;
  /** When set, the capability card becomes a link (opens in a new tab). */
  href?: string;
};

/*
  A paragraph body, optionally with inline links. A plain string renders as text;
  an array renders each part in order, where an object becomes an <a> (new tab
  for off-site links, same tab for site-relative ones).
*/
export type RichText = string | Array<string | { text: string; href: string }>;

export type PlatformSecondaryCta = {
  label: string;
  /** Link target, when the CTA is a plain link. */
  href?: string;
  /** When set, the CTA is a button that opens this contact-modal form instead. */
  modal?: ContactModalId;
  /** Renders the pulsing red "under attack" treatment (Incident Response). */
  crisis?: boolean;
};

export type PlatformService = {
  slug: string;
  number: string;
  title: string;
  /** Short line shown under the title in the hero. */
  lead: string;
  /** Longer hero paragraph. */
  description: string;
  /** ~150-char meta/OG description for search + social (avoids SERP truncation). */
  metaDescription: string;
  cta: PlatformCtaKind;
  ctaLabel: string;
  secondaryCta?: PlatformSecondaryCta;
  processLabel?: string;
  process?: PlatformItem[];
  capabilities: PlatformItem[];
  /** Free-form body paragraphs, each with a heading. Rendered after the hero. */
  sections: { heading: string; body: RichText }[];
  quote?: { text: string; author: string };
  closingHeading: string;
};

export const platformServices: Record<string, PlatformService> = {
  "incident-response": {
    slug: "incident-response",
    number: "01",
    title: "Incident Response",
    lead: "Rapid containment and recovery when an incident is underway.",
    description:
      "When a security incident is underway, Quantstamp works alongside your team to contain the compromise, remove the attacker, and restore operations. Our engagements span the full incident lifecycle, from the first indicator through post-incident hardening.",
    metaDescription:
      "Quantstamp's web3 incident response contains active attacks, removes the attacker, and restores operations, from first indicator to hardening.",
    cta: "audit",
    ctaLabel: "Talk to our response team",
    secondaryCta: {
      label: "Hacked? Contact us immediately",
      modal: "incident",
      crisis: true,
    },
    processLabel: "How we respond",
    process: [
      {
        name: "Contain",
        description:
          "We isolate the affected systems and revoke the attacker's access to limit the scope of the compromise.",
      },
      {
        name: "Eradicate",
        description:
          "We remove the attacker's foothold, rotate exposed credentials, and confirm the root cause of the incident.",
      },
      {
        name: "Recover",
        description:
          "We restore normal operations, preserve the evidence your stakeholders require, and harden the systems involved to prevent recurrence.",
      },
    ],
    capabilities: [
      {
        name: "Live Incident Response",
        description:
          "Active, hands-on response while an intrusion is in progress. Our team scopes the incident, contains it, and restores normal operations.",
      },
      {
        name: "On-Chain Exploit & Fund-Loss Response",
        description:
          "When smart contracts are under active exploitation, our team traces the attack on-chain and coordinates the containment and recovery steps that matter most in the critical early hours.",
      },
      {
        name: "Key & Signer Compromise",
        description:
          "A compromised signing key or multisig operator is an existential risk to a protocol. We contain the exposure, rotate the affected keys, and secure the access paths to your treasury.",
      },
      {
        name: "Source-Code & Asset Leaks",
        description:
          "When source code, deployment credentials, or other sensitive assets are exposed, our team traces the leak, contains it, and hardens your systems against re-exposure.",
      },
      {
        name: "Compromised-Device Forensics",
        description:
          "Forensic analysis of the endpoints and accounts involved, so your team understands the root cause, the duration of the compromise, and its full extent.",
      },
      {
        name: "IR Readiness",
        description:
          "Response playbooks, tabletop exercises, and pre-arranged support that prepare your team to act decisively in the first hour of an incident.",
      },
    ],
    sections: [
      {
        heading: "Built for high-value web3 targets",
        body: "Most incident-response providers are built around stolen customer records. The teams Quantstamp works with have more at stake: smart contracts, signing keys, treasuries, and the deployment pipelines that reach them. Our response engagements are designed for these web3-specific incidents, informed by the same adversarial expertise our auditors apply to protocol code.",
      },
      {
        heading: "What recovery means at Quantstamp",
        body: "Restoring systems to operation is only part of the work. A Quantstamp incident-response engagement also delivers a clear root-cause analysis, the documented evidence your stakeholders and partners need to see, and concrete hardening recommendations, so the same weakness cannot be exploited again.",
      },
      {
        heading: "Preparing before an incident occurs",
        body: [
          "The teams that recover well are the ones that decided who does what before the alert fired. Our ",
          { text: "guide to the first 24 hours after a protocol exploit", href: "/incident-response-guide" },
          " sets out the hour-by-hour sequence, the roles to assign, and the evidence to preserve, so your team can rehearse the response rather than improvise it.",
        ],
      },
    ],
    quote: {
      text: "Quantstamp diagnosed the root cause rapidly and put systems in place to prevent future attacks and reassure our stakeholders.",
      author: "CEO, multi-billion-dollar protocol",
    },
    closingHeading: "Facing an active incident, or preparing your team before one occurs?",
  },

  "opsec-review": {
    slug: "opsec-review",
    number: "02",
    title: "OpSec Review",
    lead: "Identify and close operational security gaps before they are exploited.",
    description:
      "Quantstamp reviews the operational security around your protocol the way a determined attacker would, across identity, data, pipelines, and cloud infrastructure. We map the full compromise chain and deliver a prioritized hardening plan your team can act on.",
    metaDescription:
      "Quantstamp reviews the operational security around your protocol across identity, data, pipelines, and cloud, then delivers a prioritized hardening plan.",
    cta: "pentest",
    ctaLabel: "Request a Review",
    capabilities: [
      {
        name: "Credentials & Access Graph",
        description:
          "We map which people and systems can reach your most sensitive assets, identify over-privileged and stale access, and recommend how to close the paths an attacker would take to your keys and funds.",
      },
      {
        name: "Compromise-Chain Analysis",
        description:
          "We trace realistic attack chains, from a single phishing email or leaked token through to your code, keys, or treasury, and identify the most cost-effective points at which to break them.",
      },
      {
        name: "Data-Exfiltration Risk Review",
        description:
          "We assess where sensitive data can leave your organization, through which channels, and what controls would prevent it, before an incident does.",
      },
      {
        name: "Sensitive-Asset Protection",
        description:
          "We evaluate the safeguards around the assets that define your protocol, including source code, signing keys, and deployment credentials, against theft, tampering, and leakage.",
      },
      {
        name: "Deployment & Supply-Chain Review",
        description:
          "We review your CI/CD pipelines, dependencies, and deployment paths, where a single compromised step can reach production and everything downstream of it.",
      },
      {
        name: "OpSec Hardening Handbook",
        description:
          "You receive a prioritized handbook that documents what to address, in what order, and why, so your team can begin remediation immediately.",
      },
    ],
    sections: [
      {
        heading: "Weaknesses that are hard to see from the inside",
        body: "A smart contract audit confirms that your code is sound. An OpSec Review examines the operation around it: your identity systems, data, pipelines, and cloud. The highest-impact weaknesses often sit in the seams between systems, such as an over-privileged service account, an unmonitored data path, or a deployment step that grants too much trust. Quantstamp maps the complete compromise chain and shows your team where it can be broken.",
      },
      {
        heading: "A plan your team can execute",
        body: "Every review concludes with a prioritized hardening handbook that sets out what to fix, in what order, and the reasoning behind each recommendation, so your team can act the moment the engagement ends.",
      },
    ],
    quote: {
      text: "Quantstamp revealed gaps we would never have found ourselves, helping protect complex operations and customer data across our global footprint.",
      author: "Founder, leading infrastructure platform",
    },
    closingHeading: "Ready to find the operational gaps before an attacker does?",
  },

  monitor: {
    slug: "monitor",
    number: "03",
    title: "Monitor",
    lead: "Continuous monitoring across data, identity, and communications.",
    description:
      "Quantstamp continuously monitors the trust surfaces around your product, across email, messaging, social, and professional networks. We track these channels for the impersonation and social-engineering campaigns that target your team and your community.",
    metaDescription:
      "Quantstamp continuously monitors your protocol, treasury, and the channels attackers use, alerting your team while there's still time to respond.",
    cta: "audit",
    ctaLabel: "Request a Demo",
    capabilities: [
      {
        name: "Communication Firewall",
        href: "https://commondefense.ai",
        description:
          "A single detection layer across email, WhatsApp, Telegram, Discord, and Slack, with built-in link analysis. It correlates activity across channels, where multi-surface social engineering becomes visible.",
      },
      {
        name: "Phishing & Social-Engineering Protection",
        description:
          "We identify the impersonation, urgency, and escalation patterns attackers use to turn a message on any channel into a signed transaction or an MFA reset.",
      },
      {
        name: "On-Chain & Treasury Monitoring",
        description:
          "We monitor the contracts and wallets that matter most, so anomalous activity is surfaced as an alert while there is still time to respond.",
      },
      {
        name: "Data-Exfiltration Monitoring",
        description:
          "We watch for sensitive data leaving through unexpected channels and alert your team while the activity can still be contained.",
      },
      {
        name: "Endpoint Protection",
        description:
          "Coverage for the devices where compromise typically begins, tuned to the way modern intrusions unfold.",
      },
    ],
    sections: [
      {
        heading: "Monitoring the protocol and the infrastructure around it",
        body: "Quantstamp's monitoring centers on the assets that define your protocol and the systems that support it. We watch the contracts and wallets that hold value for anomalous on-chain activity, track the channels through which sensitive data could leave your organization, and maintain coverage on the endpoints where intrusions most often begin. Together, these provide early warning across the surfaces most likely to be targeted, while there is still time to respond.",
      },
      {
        heading: "Extending coverage to communication channels",
        body: [
          "Attackers also reach your team through email, messaging, and social channels, which most security stacks leave uninstrumented. The ",
          { text: "Communication Firewall", href: "https://commondefense.ai" },
          " correlates activity across those channels, where multi-surface social engineering becomes visible, and complements the protocol- and infrastructure-level monitoring above.",
        ],
      },
    ],
    quote: {
      text: "Quantstamp's monitoring gave us early warning on activity we would have missed on our own, with enough time to respond before it became an incident.",
      author: "Security Lead, DeFi protocol",
    },
    closingHeading: "Ready to detect threats to your protocol before they become incidents?",
  },

  certification: {
    slug: "certification",
    number: "04",
    title: "Certification",
    lead: "A third-party attestation of your organization's security posture.",
    description:
      "Quantstamp provides a third-party attestation of your security posture across identity, communications, and operational controls. You can present it to enterprise buyers, auditors, and partners when they need to understand how your organization defends against modern threats.",
    metaDescription:
      "Quantstamp provides a third-party attestation of your web3 organization's security posture, including certification against the SEAL framework.",
    cta: "audit",
    ctaLabel: "Get Certified",
    capabilities: [
      {
        name: "Data Protection & DLP",
        description:
          "How your organization classifies, controls, and prevents the loss of sensitive data, verified against a defined standard.",
      },
      {
        name: "Key & Asset Protection",
        description:
          "The safeguards protecting the assets that define your value, including signing keys, deployment credentials, source code, and treasury access.",
      },
      {
        name: "Identity & Access",
        description:
          "Your authentication, authorization, and access model, assessed against least-privilege principles and the access paths that matter most.",
      },
      {
        name: "DevOps & Supply-Chain Integrity",
        description:
          "The integrity of your CI/CD pipelines, dependencies, and deployment process, where a single weak link can reach production.",
      },
      {
        name: "Endpoint & Ransomware Resilience",
        description:
          "Your endpoint coverage and your ability to detect, contain, and recover from ransomware.",
      },
      {
        name: "Communications & Email",
        description:
          "The resilience of the communication channels attackers target first: email and messaging.",
      },
    ],
    sections: [
      {
        heading: "How certification works",
        body: "Certification assesses your organization against a defined set of security criteria, so a result carries the same meaning to everyone who reads it. Your organization either meets the standard and receives an attestation, or receives a gap report that identifies exactly what to remediate before reassessment.",
      },
      {
        heading: "SEAL certification",
        body: [
          "Quantstamp can also certify your organization against the ",
          { text: "SEAL framework", href: "https://frameworks.securityalliance.org/certs/overview/" },
          ", the standardized security standard for web3 organizations. It scores six independently scoped domains: multisig operations, treasury operations, incident response, DevOps and infrastructure, DNS and registrar, and identity and accounts, and results in an on-chain attestation you can share with partners and buyers.",
        ],
      },
    ],
    quote: {
      text: "A Quantstamp attestation is the fastest way to show enterprise buyers that our security holds. They can read it in seconds and move forward.",
      author: "Head of Security, web3 infrastructure company",
    },
    closingHeading: "Ready to give buyers a clear answer on your security posture?",
  },

  vciso: {
    slug: "vciso",
    number: "05",
    title: "vCISO",
    lead: "Senior security leadership on call for your team.",
    description:
      "Quantstamp provides virtual CISO leadership for web3 teams that need experienced security direction without a full-time hire. Our advisors set your security roadmap, guide day-to-day decisions, and keep your program on track between engagements.",
    metaDescription:
      "Quantstamp's vCISO gives web3 teams senior security leadership on call, from security roadmap to incident readiness, without a full-time hire.",
    cta: "audit",
    ctaLabel: "Talk to an advisor",
    capabilities: [
      {
        name: "Security Roadmap & Strategy",
        description:
          "We define a prioritized security roadmap aligned with your protocol's risk profile, growth stage, and the commitments you have made to users and partners.",
      },
      {
        name: "Architecture & Design Review",
        description:
          "We review new systems and significant changes before they ship, so security is considered at the design stage rather than after deployment.",
      },
      {
        name: "Third-Party & Vendor Risk",
        description:
          "We assess the security of the vendors, integrations, and dependencies your operation relies on, and recommend how to manage the risk they introduce.",
      },
      {
        name: "Compliance & Audit Readiness",
        description:
          "We help your team prepare for frameworks such as SOC 2 and ISO 27001, and translate their requirements into concrete engineering work.",
      },
      {
        name: "Incident Preparedness",
        description:
          "We put response plans, escalation paths, and tabletop exercises in place, so your team is ready to act before an incident occurs.",
      },
      {
        name: "Key Management & Signing Architecture",
        description:
          "We review how your keys, multisigs, and signing workflows are structured, and recommend improvements to custody, thresholds, and access so the paths to your treasury are hard to abuse.",
      },
    ],
    sections: [
      {
        heading: "Security leadership without a full-time hire",
        body: "Many web3 teams need experienced security judgment long before they can justify a full-time CISO. A Quantstamp vCISO engagement gives your team access to that leadership on an ongoing basis, setting direction, guiding decisions, and keeping your security program moving forward between larger pieces of work.",
      },
      {
        heading: "Grounded in your actual environment",
        body: "Because Quantstamp audits, monitors, and hardens web3 systems every day, our vCISO guidance is grounded in the risks that affect protocols like yours rather than generic checklists. That context lets your team focus its effort where it will reduce the most risk.",
      },
    ],
    quote: {
      text: "Having Quantstamp as our trusted security advisors gives banks and partners the assurance they need to trust our infrastructure, so we can scale up confidently.",
      author: "CTO, stablecoin payments provider",
    },
    closingHeading: "Ready to add senior security leadership to your team?",
  },
};

export const platformServiceOrder = [
  "incident-response",
  "opsec-review",
  "monitor",
  "certification",
  "vciso",
] as const;
